import { usingKeyboardAtom } from "@/atoms/threadsAtoms.ts";
import { Archive2, Sparkle } from "@/components/icons/customIcons.tsx";
import {
  FadeScrollbarThumb,
  hideNativeScrollbarClass,
  useFadeScrollbar,
} from "@/components/layout/contentLayout.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import {
  useSurfacePreferences,
  useUpdateSurfacePreferences,
} from "@/components/preferences/usePreferences.tsx";
import { ThreadCard } from "@/components/threads/ThreadCard.tsx";
import { ThreadRow } from "@/components/threads/ThreadRow.tsx";
import { useMarkThreadDone } from "@/components/threads/useMarkThreadDone.ts";
import { getThreadListSignature } from "@/components/threads/util.ts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { useThreadFilter, useThreads } from "@/hooks/useThreads.tsx";
import { filterLabels, groupLabels, type ThreadFilterMode } from "@/shared/threadFilters.ts";
import { layoutSwitchLabels } from "@/shared/threadLayout.ts";
import { sortSwitchActions } from "@/shared/threadSort.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import { flattenThreadListPages } from "@/shared/threadListPages.ts";
import { useAtom } from "jotai";
import { useMarkThreadsListViewed } from "@/hooks/useMarkThreadsListViewed.tsx";
import { useGateValue } from "@statsig/react-bindings";
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Inbox,
  MessageCircle,
  Rows2,
  Rows3,
  Zap,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useSplitPanelColumn } from "@/components/layout/SplitPanel/SplitPanelLayout.tsx";
import { useRestoreScrollPosition } from "@/hooks/useRestoreScrollPosition.ts";
import { useShortcut } from "@/components/keyboard/useShortcut.ts";
import { useKeyboardShortcutsEnabled } from "@/components/keyboard/useKeyboardShortcuts.ts";
import { useInView } from "react-intersection-observer";
import { cn, isCoarsePointer } from "@/lib/utils.ts";

const filterIcons: Record<ThreadFilterMode, ReactNode> = {
  active: <Sparkle className="size-4" />,
  routines: <Zap className="size-4" />,
  all: <Inbox className="size-4" />,
  archived: <Archive2 className="size-4" />,
  external: <MessageCircle className="size-4" />,
};

export const ThreadFilterDropdown = ({
  variant = "panel",
}: {
  /**
   * `"panel"` — the compact sans-serif label in the threads panel's own top bar.
   * `"title"` — the large serif treatment used when this stands in for the month
   * title in the calendar header (mobile Chats tab), so the two read the same.
   */
  variant?: "panel" | "title";
} = {}) => {
  const isTitle = variant === "title";
  const { filterMode, setFilterMode } = useThreadFilter();
  // The External tab is part of the external-threads feature, so it rides the
  // same gate (ramped to everyone) rather than being admin-only.
  const showExternal = useGateValue("visible_external_threads");
  // The card/compact layout preference is shared with the /threads home
  // page (`useSurfacePreferences("home")`) — picking "compact" in the
  // sidebar dropdown also flips the home page's grid to row mode and vice
  // versa.
  const { layoutMode, needsAttentionFirst } = useSurfacePreferences("home");
  const updateHomePreferences = useUpdateSurfacePreferences("home");
  const column = useSplitPanelColumn();

  const topModes: ThreadFilterMode[] = ["active", "routines", "all"];
  const bottomModes: ThreadFilterMode[] = showExternal ? ["external", "archived"] : ["archived"];

  const handleNeedsAttentionToggle = () => {
    void updateHomePreferences({ needsAttentionFirst: !needsAttentionFirst }).catch(() => {});
  };

  const handleLayoutToggle = () => {
    const next = layoutMode === "large" ? "compact" : "large";
    void updateHomePreferences({ layoutMode: next });
    // Cards only fit at the column's resting width, and narrowing the column is
    // what put the list in rows. Choosing them back has to undo that too, or the
    // switcher would promise a layout the current width cannot show.
    //
    // Only where a drag actually narrowed it: the width is shared with the
    // thread detail screen's divider, so resetting it unasked would discard a
    // width set somewhere else entirely.
    if (next === "large" && column?.isLeftNarrowed) column.restoreLeftWidth();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1 cursor-pointer select-none outline-none decoration-1 underline-offset-2 hover:underline focus-visible:underline min-w-0 [&[data-state=open]_.chevron]:rotate-180"
        aria-label={`Filter: ${filterLabels[filterMode]}`}
        data-statsig-name="calendar-threads-filter-dropdown"
      >
        <span
          className={cn(
            // min-w-0 is required for truncate to engage: as a flex item this span
            // defaults to min-width:auto, which refuses to shrink below its text and
            // widens the trigger instead of ellipsizing. The chevron stays shrink-0.
            "min-w-0 truncate",
            // text-sm/font-medium matches the compact thread-row titles' size and
            // the design system's Button label weight.
            isTitle ? "font-serif text-2xl @md:text-3xl tracking-tight" : "text-sm font-medium",
          )}
        >
          {filterLabels[filterMode]}
        </span>
        <ChevronDown
          className={cn(
            "chevron shrink-0 transition-transform duration-200",
            isTitle ? "size-5 text-muted-foreground" : "size-4",
          )}
          strokeWidth={isTitle ? 2 : 1.5}
        />
      </DropdownMenuTrigger>
      {/* Wide enough for the longest item, "Sort Needs attention first", which
          measures 166px in Inter at this size and needs 214px with the icon,
          gap and padding around it. At 208px it wrapped to two lines. */}
      <DropdownMenuContent align="start" className="w-56" sideOffset={6}>
        <DropdownMenuLabel className="text-xs text-muted-foreground font-medium">
          View options
        </DropdownMenuLabel>
        {topModes.map((mode) => (
          <DropdownMenuItem
            key={mode}
            className="py-2.5 gap-2"
            onSelect={() => setFilterMode(mode)}
          >
            {filterIcons[mode]}
            <span className="flex-1">{filterLabels[mode]}</span>
            {filterMode === mode && <Check className="size-4 stroke-2 ml-auto" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {bottomModes.map((mode) => (
          <DropdownMenuItem
            key={mode}
            className="py-2.5 gap-2"
            onSelect={() => setFilterMode(mode)}
          >
            {filterIcons[mode]}
            <span className="flex-1">{filterLabels[mode]}</span>
            {filterMode === mode && <Check className="size-4 stroke-2 ml-auto" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="py-2.5 gap-2"
          onSelect={handleNeedsAttentionToggle}
          data-statsig-name="calendar-threads-needs-attention-toggle"
        >
          <ArrowUpDown className="size-4" />
          {needsAttentionFirst ? sortSwitchActions.recent : sortSwitchActions.needsAttentionFirst}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="py-2.5 gap-2"
          onSelect={handleLayoutToggle}
          data-statsig-name="calendar-threads-layout-toggle"
        >
          {layoutMode === "large" ? <Rows3 className="size-4" /> : <Rows2 className="size-4" />}
          {layoutMode === "large" ? layoutSwitchLabels.compact : layoutSwitchLabels.large}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Caption naming one group of the list. Callers show captions only once
 * something is pinned, since the filter dropdown already names a list's sole
 * group; the `id` lets the group below point back at this as its name.
 */
const GroupCaption = ({
  id,
  children,
  className,
}: {
  id: string;
  children: ReactNode;
  className?: string;
}) => (
  // `px-1` matches the empty-state copy in this list, so both sit on the same
  // left edge as the compact rows' leading marks.
  <div
    id={id}
    className={cn("shrink-0 px-1 pb-2 text-xs font-medium text-muted-foreground", className)}
  >
    {children}
  </div>
);

/**
 * Content-only threads list that lives inside the calendar's left panel.
 */
export const ThreadList = ({
  onSelect,
  openThreadId = null,
  showFilterBar,
  leading,
}: {
  onSelect: (threadId: number) => void;
  /** The thread the host is showing, so its row reads as the open one. */
  openThreadId?: number | null;
  showFilterBar: boolean;
  /** Rides at the top of the scroller, above the filter bar — the panel's nav
   *  list, which scrolls with the threads rather than sitting over them. */
  leading?: ReactNode;
}) => {
  // Mark the threads list as viewed whenever it mounts — the list only mounts
  // while the threads panel is open, so a mount effect is the right "viewed"
  // signal. Clears the completed contribution to the badge.
  const { mutate: markThreadsListViewed } = useMarkThreadsListViewed();
  // `mutate` is a stable reference, so this fires only on mount — the
  // "viewed on show" signal we want. The dep is listed (rather than `[]`) to
  // satisfy Biome's useExhaustiveDependencies, which `check-fix --unsafe`
  // re-adds anyway.
  useEffect(() => {
    markThreadsListViewed();
  }, [markThreadsListViewed]);

  // Read filter from the URL-backed `useThreadFilter()` so this sidebar list
  // and the main /threads page stay in sync — picking "Archived" here also
  // affects the home page (and vice versa).
  const { filterMode, filterParams } = useThreadFilter();
  const { layoutMode, needsAttentionFirst } = useSurfacePreferences("home");
  const updateHomePreferences = useUpdateSurfacePreferences("home");
  // Cards need the sidebar's full resting width to read; narrowed at all, they
  // are a title squeezed beside a body preview with room for neither. So
  // narrowing commits the preference to rows.
  //
  // Committed rather than merely rendered: the switcher reads the same
  // preference, so a presentation-only override would leave it offering "switch
  // to compact" while showing rows — and widening again would spring cards back
  // on a user who had since decided they preferred the list.
  //
  // On the transition into narrow, not on the state. A failed write is rolled
  // back onto the very preference this reads, so a write keyed on the state
  // would re-satisfy its own condition and retry against an endpoint that is
  // already failing, without limit.
  const isNarrowed = useSplitPanelColumn()?.isLeftNarrowed ?? false;
  const wasNarrowed = useRef(isNarrowed);
  useEffect(() => {
    const justNarrowed = isNarrowed && !wasNarrowed.current;
    wasNarrowed.current = isNarrowed;
    if (!justNarrowed || layoutMode !== "large") return;
    // Rejections are handled by the mutation's own `onError`, which rolls the
    // optimistic write back; this only keeps the promise from floating.
    void updateHomePreferences({ layoutMode: "compact" }).catch(() => {});
  }, [isNarrowed, layoutMode, updateHomePreferences]);
  const { threadPages, isLoading, isFetchingNextPage, error, hasNextPage, loadMore } = useThreads({
    ...filterParams,
    limit: 36,
    needsAttentionFirst,
  });
  // The server serves the two groups, scoped to the active filter; flattening
  // drops a thread that two page fetches straddled. `threads` is the order
  // `j`/`k` walks — pins first, so the captions don't interrupt navigation.
  const { pinned, rest } = useMemo(() => flattenThreadListPages(threadPages), [threadPages]);
  const threads = useMemo(() => [...pinned, ...rest], [pinned, rest]);

  // Keyboard focus within the list (Linear-style): `j`/`k` move the focused row,
  // hover sets it too, `Enter`/`o` opens the focused thread, `e` archives it. The
  // list only mounts while the threads panel is open, so these keys are naturally
  // scoped — and the calendar's own `j`/`k` yield to them.
  const [focusedId, setFocusedId] = useState<number | null>(null);
  // The keyboard-focus ring is suppressed on touch-first devices (see
  // `isCoarsePointer` — a hybrid laptop with a mouse still shows the ring).
  // The focus ring is the visual affordance for `j`/`k` navigation, so only show
  // it when the user has single-key shortcuts enabled — otherwise the keys do
  // nothing and a lingering ring is just confusing. It's also gated on actual
  // keyboard use: `focusedId` follows mouse hover too (so `j`/`k` continue from
  // the hovered row), and without this gate the ring would trail the pointer.
  // This list is the atom's writer: `j`/`k` turn it on, hover turns it off.
  const shortcutsEnabled = useKeyboardShortcutsEnabled();
  const [usingKeyboard, setUsingKeyboard] = useAtom(usingKeyboardAtom);
  const showFocusRing = shortcutsEnabled && usingKeyboard && !isCoarsePointer();
  // Keep focus pointing at a real row as the list loads/changes; default to first.
  useEffect(() => {
    setFocusedId((cur) => {
      if (threads.length === 0) return null;
      return cur != null && threads.some((t) => t.id === cur) ? cur : threads[0].id;
    });
  }, [threads]);
  const moveFocus = useCallback(
    (dir: 1 | -1) => {
      setUsingKeyboard(true);
      setFocusedId((cur) => {
        if (threads.length === 0) return null;
        const idx = threads.findIndex((t) => t.id === cur);
        const next = Math.min(threads.length - 1, Math.max(0, (idx < 0 ? 0 : idx) + dir));
        return threads[next].id;
      });
    },
    [threads, setUsingKeyboard],
  );
  const { markDone: markFocusedDone } = useMarkThreadDone({ threadId: focusedId ?? 0 });
  useShortcut("threads.prev", () => moveFocus(-1), { deps: [moveFocus] });
  useShortcut("threads.next", () => moveFocus(1), { deps: [moveFocus] });
  useShortcut(
    "threads.open",
    () => {
      if (focusedId != null) onSelect(focusedId);
    },
    { enabled: focusedId != null, deps: [focusedId, onSelect] },
  );
  useShortcut(
    "threads.archive",
    () => {
      if (focusedId != null) markFocusedDone();
    },
    { enabled: focusedId != null, deps: [focusedId, markFocusedDone] },
  );

  // Infinite scroll — fetch the next page as the sentinel nears the bottom of
  // the panel's own scroll container. The sentinel scrolls inside that container
  // rather than the viewport, so the observer has to take this node as its root
  // or it never intersects. The same node drives the custom fading scrollbar via
  // useFadeScrollbar.
  const { setNode: setFadeNode, node: scrollNode, thumb } = useFadeScrollbar();
  // Restore scroll position when returning to the calendar from a thread's page.
  // The calendar unmounts on navigation, so the list remounts on the way back and
  // would otherwise jump to the top (BIG-5419). useRestoreScrollPosition wants a
  // RefObject, but useFadeScrollbar gives a callback ref + state node — so mirror
  // the node into a ref and feed both from one merged callback ref.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const setScrollNode = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      setFadeNode(node);
    },
    [setFadeNode],
  );
  const getSignature = useCallback(() => getThreadListSignature(threads), [threads]);
  useRestoreScrollPosition({
    // Keyed per layout so a card-mode offset isn't restored onto the compact list
    // (different row heights).
    storageKey: `calendar-thread-panel-${layoutMode}`,
    containerRef: scrollContainerRef,
    getSignature,
  });
  const [loadMoreTriggerRef] = useInView({
    threshold: 0,
    skip: isFetchingNextPage || !hasNextPage,
    root: scrollNode,
    rootMargin: "0px 0px 300px 0px",
    onChange: (inView) => {
      if (inView) loadMore();
    },
  });

  // Unique per mounted list, so two lists on one page can't both claim an id.
  const captionIdPrefix = useId();
  const pinnedCaptionId = `${captionIdPrefix}-pinned`;
  const restCaptionId = `${captionIdPrefix}-rest`;

  const isCompact = layoutMode === "compact";
  /**
   * Rows for one group of the list. The pinned group and the remainder render
   * identically, so the rows are built here rather than once per group per
   * layout mode. `labelledBy` is the id of the caption above, which is what
   * announces the group to a screen reader — the badge that used to carry that
   * for each row is gone, and a caption on its own is only loose text. Omitted
   * when the list is a single uncaptioned group.
   */
  const renderGroup = (group: GetThreadResp[], labelledBy?: string) => {
    const rows = group.map((thread) => (
      <div
        key={thread.id}
        onMouseEnter={() => {
          setUsingKeyboard(false);
          setFocusedId(thread.id);
        }}
        className={cn(
          "rounded-xl",
          // md+ bleeds the row's hover surface into the panel gutter so the row
          // text stays flush with the list's left edge; mobile cards stay inside
          // the gutter (rounded to match the card).
          isCompact && "md:-mx-2 md:rounded-lg",
          // Keyboard-focus ring only when shortcuts are enabled and on pointer
          // devices — on touch, `onMouseEnter` fires on tap and the ring would
          // linger after backing out of a thread (BIG-5364). Hover clears the
          // keyboard flag for the same reason: `focusedId` follows the pointer
          // too, so leaving the flag set would drag the ring behind the mouse.
          thread.id === focusedId &&
            showFocusRing &&
            "ring-2 ring-primary ring-offset-1 ring-offset-background",
        )}
      >
        {isCompact ? (
          <ThreadRow thread={thread} onSelect={onSelect} isOpen={thread.id === openThreadId} />
        ) : (
          <ThreadCard thread={thread} isSelected={false} onSelect={onSelect} />
        )}
      </div>
    ));

    // Cards are spaced apart at every width (gap-3). Compact rows match that
    // below md, then tighten to a 4px gap from md up, where the row is 28px and
    // its hover surface sits flush to the row box — a 32px pitch.
    const className = cn("flex flex-col gap-3", isCompact && "md:gap-1");

    // Spelled as two elements rather than one with a conditional role: an
    // unnamed group is noise in the accessibility tree, and the caption is the
    // only thing that can name one.
    if (labelledBy === undefined) return <div className={className}>{rows}</div>;
    return (
      // biome-ignore lint/a11y/useSemanticElements: <fieldset> groups form controls and brings its own box and form semantics; these are thread links, so `role="group"` named by the caption is the ARIA this calls for.
      <div role="group" aria-labelledby={labelledBy} className={className}>
        {rows}
      </div>
    );
  };

  const renderBody = () => {
    if (isLoading && threads.length === 0) {
      return <div className="text-sm text-muted-foreground px-1 py-2">Loading…</div>;
    }
    if (error) {
      return <div className="text-sm text-destructive px-1 py-2">Couldn't load threads.</div>;
    }
    if (threads.length === 0) {
      return (
        <div className="text-sm text-muted-foreground px-1 py-2">
          {filterMode === "active"
            ? "No active threads."
            : `No ${filterLabels[filterMode].toLowerCase()}.`}
        </div>
      );
    }
    const hasPinnedGroup = pinned.length > 0;
    // Tied to one flag so the caption and the group naming it cannot drift: an
    // `aria-labelledby` pointing at an id nothing renders names nothing.
    const showRestCaption = hasPinnedGroup && rest.length > 0;
    return (
      // Every group holds a fixed slot and carries its own condition, rather
      // than a ternary choosing between two shapes. React reconciles a slot by
      // element type, so swapping a fragment for a div when the first pin
      // appears rebuilds every row's DOM and discards the state riding on it —
      // an in-progress inline rename, an open row menu. The unpinned group
      // therefore renders unconditionally; with no rows it costs an empty box.
      <>
        {hasPinnedGroup && <GroupCaption id={pinnedCaptionId}>Pinned</GroupCaption>}
        {hasPinnedGroup && renderGroup(pinned, pinnedCaptionId)}
        {showRestCaption && (
          <GroupCaption id={restCaptionId} className="pt-4">
            {groupLabels[filterMode]}
          </GroupCaption>
        )}
        {renderGroup(rest, showRestCaption ? restCaptionId : undefined)}
        {hasNextPage && (
          <div ref={loadMoreTriggerRef} className="py-4 flex justify-center">
            <Loader size="sm" />
          </div>
        )}
      </>
    );
  };

  // The scroller spans the panel's full width and insets its own contents by
  // `--panel-gutter`, so the scrollbar sits against the panel edge with a gutter
  // from the cards — matching the thread detail view — rather than flush against
  // them. The panel body adds no padding of its own, so this is the only gutter
  // in play and the rows' hover surfaces bleed back out through it.
  return (
    // `relative` hosts the custom fading scrollbar thumb pinned to the edge.
    <div className="relative flex flex-col flex-1 min-h-0">
      <div
        ref={setScrollNode}
        className={cn(
          // Generous bottom padding so the last card can scroll clear of the
          // create-or-ask pill that floats over the panel's bottom edge.
          "flex flex-col flex-1 min-h-0 overflow-y-auto px-(--panel-gutter) pb-28",
          hideNativeScrollbarClass,
        )}
      >
        {/* Everything between the panel's header and its footer scrolls as one
            column, so the views the sidebar can pivot to ride inside the
            scroller with the threads rather than staying pinned above them.
            The panel supplies them; the scroller only has to carry them. */}
        {leading}
        {/* Filter bar has no top padding — the dropdown's cap line matches the
            mini calendar's top edge on the calendar tab, so swapping tabs
            doesn't bounce. */}
        {showFilterBar && (
          // Deliberately different from the `pb-4` the nav list above uses,
          // because the two gaps are measured against different things. Above
          // the title sits a filled pill whose ink is its box; below it sits a
          // row carrying 4px of its own padding before its first glyph. Ink to
          // ink — which is what the eye measures — `pb-3` here against `pb-4`
          // there lands both at 18px. This tracks the row's internal padding,
          // which was 8px back when the rows were 36px tall, so re-measure both
          // if the row height moves.
          <div className="shrink-0 flex items-center pb-3">
            <ThreadFilterDropdown />
          </div>
        )}
        {renderBody()}
      </div>
      <FadeScrollbarThumb thumb={thumb} />
    </div>
  );
};
